import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  UserPlus, PencilSimple, MapPinPlus, CurrencyDollar, Receipt, ArrowUUpLeft,
  Package, CheckCircle, Trash, SignIn, DownloadSimple, UploadSimple, MapPin, UserMinus, Shield,
} from "@phosphor-icons/react";
import { fmtDate } from "@/lib/dates";

const ACTION_META = {
  "auth.login": { icon: SignIn, tone: "text-neutral-500" },
  "user.create": { icon: UserPlus, tone: "text-emerald-700" },
  "user.update": { icon: PencilSimple, tone: "text-amber-700" },
  "user.delete": { icon: UserMinus, tone: "text-red-700" },
  "region.create": { icon: MapPinPlus, tone: "text-emerald-700" },
  "region.delete": { icon: MapPin, tone: "text-red-700" },
  "record.create": { icon: Package, tone: "text-neutral-900" },
  "record.return": { icon: CheckCircle, tone: "text-emerald-700" },
  "record.edit": { icon: PencilSimple, tone: "text-amber-700" },
  "record.delete": { icon: Trash, tone: "text-red-700" },
  "record.import": { icon: UploadSimple, tone: "text-neutral-900" },
  "record.receipt": { icon: DownloadSimple, tone: "text-neutral-500" },
  "finance.invoice": { icon: Receipt, tone: "text-emerald-700" },
  "finance.uninvoice": { icon: ArrowUUpLeft, tone: "text-amber-700" },
  "finance.report": { icon: DownloadSimple, tone: "text-neutral-500" },
};

// Actions that only manager (or, in the case of finance.*, finance/manager) can perform.
// Used for the "Manager actions" quick filter.
const MANAGER_ACTIONS = new Set([
  "record.edit",
  "record.delete",
  "finance.invoice",
  "finance.uninvoice",
]);

export default function AuditPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("all");
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [managerOnly, setManagerOnly] = useState(false);
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, size: PAGE_SIZE };
      // When "Manager actions" toggle is on we don't send the per-action filter;
      // instead we grab a broader window and filter client-side (action list + 30 days).
      if (!managerOnly && action !== "all") params.action = action;
      if (type !== "all") params.target_type = type;
      const res = await api.get("/audit", { params });
      // Backend returns `{items,total,page,size}` under paginated mode.
      const d = res.data;
      setEntries(d.items || []);
      setTotal(d.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action, type, managerOnly, page]);
  // Reset to page 1 when filters change (not page itself).
  // eslint-disable-next-line
  useEffect(() => { setPage(1); }, [action, type, managerOnly, search]);

  const filtered = useMemo(() => {
    let out = entries;
    if (managerOnly) {
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
      out = out.filter((e) =>
        MANAGER_ACTIONS.has(e.action) &&
        new Date(e.created_at).getTime() >= cutoff,
      );
    }
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((e) =>
        e.summary.toLowerCase().includes(q) ||
        e.actor_name.toLowerCase().includes(q) ||
        e.action.toLowerCase().includes(q),
      );
    }
    return out;
  }, [entries, search, managerOnly]);

  const groups = useMemo(() => {
    const out = {};
    for (const e of filtered) {
      const d = new Date(e.created_at);
      const key = d.toISOString().slice(0, 10);
      if (!out[key]) out[key] = [];
      out[key].push(e);
    }
    return out;
  }, [filtered]);

  return (
    <div className="min-h-screen">
      <div className="border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="px-10 py-6 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-eyebrow">Trail</div>
            <h1 className="font-display text-4xl tracking-tight leading-none mt-2">Audit Log</h1>
            <p className="text-sm text-neutral-500 mt-2">Every mutation, signed with actor and timestamp.</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setManagerOnly((v) => !v)}
              data-testid="audit-manager-toggle"
              title="Show only Edit/Delete/Invoice actions from the last 30 days"
              className={`h-10 px-4 border text-xs font-mono uppercase tracking-wider flex items-center gap-2 transition-colors ${
                managerOnly
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900"
              }`}
            >
              <Shield size={14} weight="bold" /> Manager actions (30 d)
            </button>
            <div className="text-right">
              <div className="label-eyebrow">Events</div>
              <div className="font-display text-3xl" data-testid="audit-count">{total.toLocaleString()}</div>
            </div>
          </div>
        </div>
        <div className="px-10 pb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input
            placeholder="Search actor, summary, action…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-10 rounded-none border-neutral-300 focus-visible:border-neutral-900 focus-visible:ring-0"
            data-testid="audit-search"
          />
          <Select value={action} onValueChange={setAction} disabled={managerOnly}>
            <SelectTrigger data-testid="audit-action-filter" className={`h-10 rounded-none border-neutral-300 ${managerOnly ? "opacity-50" : ""}`}><SelectValue placeholder="Action" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {Object.keys(ACTION_META).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="audit-target-filter" className="h-10 rounded-none border-neutral-300"><SelectValue placeholder="Target" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All targets</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="region">Region</SelectItem>
              <SelectItem value="record">Record</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="p-10 space-y-8" data-testid="audit-timeline">
        {loading && <div className="text-neutral-400 text-sm">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="border border-neutral-200 bg-white p-10 text-center text-neutral-400 text-sm">
            No activity yet.
          </div>
        )}
        {Object.entries(groups).map(([day, items]) => (
          <section key={day}>
            <div className="flex items-center gap-3 mb-3">
              <div className="label-eyebrow">{fmtDate(day)}</div>
              <div className="flex-1 h-px bg-neutral-200" />
              <div className="text-xs font-mono text-neutral-500">{items.length}</div>
            </div>
            <ol className="relative border-l border-neutral-200 ml-2 space-y-3">
              {items.map((e) => {
                const meta = ACTION_META[e.action] || { icon: Package, tone: "text-neutral-500" };
                const Icon = meta.icon;
                return (
                  <li key={e.id} className="pl-6 relative" data-testid={`audit-entry-${e.id}`}>
                    <span className={`absolute -left-[9px] top-1 w-4 h-4 flex items-center justify-center bg-white border border-neutral-900 ${meta.tone}`}>
                      <Icon size={10} weight="bold" />
                    </span>
                    <div className="border border-neutral-200 bg-white p-3 hover:border-neutral-900 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm">{e.summary}</div>
                          <div className="text-xs text-neutral-500 mt-1 font-mono truncate">
                            {e.actor_name} · {e.actor_role} · {e.action}
                          </div>
                        </div>
                        <div className="text-xs font-mono text-neutral-500 shrink-0">
                          {new Date(e.created_at).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
        {/* Pager — server-side pagination (25/page). */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between pt-4 border-t border-neutral-200" data-testid="audit-pager">
            <div className="text-xs font-mono text-neutral-500">
              {`${((page - 1) * PAGE_SIZE) + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total.toLocaleString()}`}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                data-testid="audit-pager-prev"
                className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-neutral-300 bg-white hover:border-neutral-900 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              <span className="text-[11px] font-mono text-neutral-600 tabular-nums">
                Page {page} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => (p * PAGE_SIZE < total ? p + 1 : p))}
                disabled={page * PAGE_SIZE >= total || loading}
                data-testid="audit-pager-next"
                className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-neutral-300 bg-white hover:border-neutral-900 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
